import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../utils/api';
import CommentsSection from './CommentsSection';
import PrintButton from './PrintButton';
import ImageModal from './ImageModal';
import Navbar from '../../../components/Navbar';

const CustomizedBookPage = () => {
  const { bookId = 1 } = useParams(); // Default to book ID 1 if not provided
  const [customizedChapters, setCustomizedChapters] = useState([]);
  const [originalSections, setOriginalSections] = useState([]);
  const [selectedChapterIdx, setSelectedChapterIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const [showNavigation, setShowNavigation] = useState(true);
  const [openSections, setOpenSections] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  
  const navigate = useNavigate();

  useEffect(() => {
    const loadCustomizedChapters = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('app_token');
        if (!token) {
          throw new Error('No authentication token');
        }

        // First, fetch the original book's section structure
        const originalBookResponse = await api.get(`/api/books/${bookId}`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        console.log('📖 Original book sections:', originalBookResponse.data.sections);
        setOriginalSections(originalBookResponse.data.sections || []);

        // Fetch customized book content from user_customized_books table
        const response = await api.get(`/api/customized-book/content?bookId=${bookId}`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          params: { _t: Date.now() }
        });

        console.log('🎯 Customized book API response:', response.data);
        
        if (response.data.status === 'completed' && response.data.chapters && response.data.chapters.length > 0) {
          // Use the chapters array from the API response (already in correct order)
          console.log('📚 Using chapters from API response:', response.data.chapters.length);
          console.log('📚 Chapters:', response.data.chapters);
          
          setCustomizedChapters(response.data.chapters);
          setLoading(false);
        } else if (response.data.status === 'completed' && response.data.customized_content) {
          // Fallback: Parse the HTML content to extract chapters
          const htmlContent = response.data.customized_content;
          console.log('📄 HTML Content length:', htmlContent.length);
          console.log('📄 HTML Content preview:', htmlContent.substring(0, 500));
          
          const chapters = parseChaptersFromHTML(htmlContent, originalBookResponse.data.sections || []);
          
          console.log('Parsed chapters from HTML:', chapters);
          setCustomizedChapters(chapters);
          setLoading(false);
        } else if (response.data.status === 'processing') {
          // Book is being generated, start polling for progress
          console.log('🔄 Book is being generated, starting progress polling');
          setLoading(false);
          setIsGenerating(true);
          startProgressPolling();
        } else if (response.data.status === 'not_found') {
          // No customized book found, don't automatically start generation
          console.log('❌ No customized content found. Status:', response.data.status);
          setLoading(false);
          // Don't throw error, just show the generation button
        } else {
          console.log('❌ No customized content found. Status:', response.data.status);
          setLoading(false);
          // Don't throw error, just show the generation button
        }
      } catch (err) {
        console.error('Error loading customized book:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadCustomizedChapters();
  }, [bookId]);

  // Function to start progress polling
  const startProgressPolling = () => {
    const pollInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('app_token');
        const response = await api.get(`/api/customized-book/progress?bookId=${bookId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const { progress, status, totalChapters } = response.data;
        setGenerationProgress(progress || 0);

        if (status === 'completed') {
          clearInterval(pollInterval);
          setIsGenerating(false);
          // Reload the customized book
          window.location.reload();
        } else if (status === 'failed') {
          clearInterval(pollInterval);
          setIsGenerating(false);
          setError('Book generation failed. Please try again.');
        }
      } catch (err) {
        console.error('Error polling progress:', err);
        clearInterval(pollInterval);
        setIsGenerating(false);
        setError('Error checking generation progress');
      }
    }, 2000); // Poll every 2 seconds

    // Cleanup interval on unmount
    return () => clearInterval(pollInterval);
  };

  // Function to generate customized book
  const handleGenerateBook = async () => {
    try {
      setIsGenerating(true);
      setError(null);

      const token = localStorage.getItem('app_token');
      const response = await api.post('/api/customized-book/start', { bookId }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('🚀 Book generation started:', response.data);

      if (response.data.status === 'processing') {
        // Start polling for progress
        startProgressPolling();
      } else if (response.data.status === 'completed') {
        // Book already exists, use the returned data
        setIsGenerating(false);
        if (response.data.chapters && response.data.chapters.length > 0) {
          setCustomizedChapters(response.data.chapters);
        } else if (response.data.customized_content) {
          const chapters = parseChaptersFromHTML(response.data.customized_content, originalSections);
          setCustomizedChapters(chapters);
        }
      }
    } catch (err) {
      console.error('Error starting book generation:', err);
      const errorMessage = err.response?.data?.error || 'Failed to start book generation';
      setError(errorMessage);
      setIsGenerating(false);
    }
  };

  // Function to parse chapters from HTML content
  const parseChaptersFromHTML = (htmlContent, originalSections) => {
    const chapters = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Find all chapter divs
    const chapterDivs = doc.querySelectorAll('.chapter');
    
    console.log('🔍 Found chapter divs:', chapterDivs.length);
    console.log('📚 Original sections:', originalSections);
    console.log('📄 HTML Content length:', htmlContent.length);
    console.log('📄 HTML Content preview:', htmlContent.substring(0, 1000));
    
    // Also try other selectors in case the structure is different
    const allDivs = doc.querySelectorAll('div');
    console.log('🔍 Total divs found:', allDivs.length);
    
    // Look for any divs that might contain chapter content
    const potentialChapters = Array.from(allDivs).filter(div => {
      const hasTitle = div.querySelector('h1, h2, h3, h4, h5, h6');
      const hasContent = div.innerHTML.length > 100; // Arbitrary length to filter out empty divs
      return hasTitle && hasContent;
    });
    
    console.log('🔍 Potential chapters found:', potentialChapters.length);
    
    // Use the original chapter divs if found, otherwise use potential chapters
    const chaptersToProcess = chapterDivs.length > 0 ? chapterDivs : potentialChapters;
    
    chaptersToProcess.forEach((chapterDiv, index) => {
      const titleElement = chapterDiv.querySelector('h2, h3, h1, h4, h5, h6');
      
      if (titleElement) {
        const title = titleElement.textContent.trim();
        
        // Clone the chapter div and remove the title element to get clean content
        const chapterClone = chapterDiv.cloneNode(true);
        const titleClone = chapterClone.querySelector('h2, h3, h1, h4, h5, h6');
        if (titleClone) {
          titleClone.remove();
        }
        
        // Get the remaining content
        const content = chapterClone.innerHTML.trim();
        
        console.log(`📖 Chapter ${index + 1}:`, { title, contentLength: content.length });
        
        // Find the matching original chapter to get its section
        let section = 'Other Chapters';
        if (originalSections && originalSections.length > 0) {
          // Simple approach: assign chapters to sections based on order
          // If we have more chapters than sections, cycle through sections
          const sectionIndex = index % originalSections.length;
          section = originalSections[sectionIndex].title;
          
          console.log(`📋 Chapter "${title}" assigned to section "${section}" (index: ${index}, sectionIndex: ${sectionIndex})`);
        } else {
          console.log(`📋 Chapter "${title}" assigned to "Other Chapters" (no original sections available)`);
        }
        
        chapters.push({
          id: `chapter-${index}`,
          title: title,
          content: content,
          image_url: null,
          section_id: section,
          order: index + 1
        });
      } else {
        console.log(`⚠️ Chapter div ${index} has no title element`);
      }
    });
    
    console.log('📚 Total chapters parsed:', chapters.length);
    return chapters;
  };

  // Function to group chapters by section
  const groupChaptersBySection = (chapters) => {
    const grouped = {};
    
    console.log('🔍 Grouping chapters by section. Total chapters:', chapters.length);
    console.log('📚 Original sections available:', originalSections);
    
    // Create a mapping from section ID to section name
    const sectionIdToName = {};
    if (originalSections && originalSections.length > 0) {
      originalSections.forEach(section => {
        sectionIdToName[section.id] = section.title;
      });
      console.log('🗺️ Section ID to Name mapping:', sectionIdToName);
    }
    
    chapters.forEach((chapter, index) => {
      let sectionName = 'Other Chapters';
      
      // Try to map sectionId to section name (note: backend returns sectionId, not section_id)
      if (chapter.sectionId && sectionIdToName[chapter.sectionId]) {
        sectionName = sectionIdToName[chapter.sectionId];
      } else if (chapter.sectionId) {
        // If we have a sectionId but no mapping, use the ID as fallback
        sectionName = `Section ${chapter.sectionId}`;
      }
      
      if (!grouped[sectionName]) {
        grouped[sectionName] = [];
      }
      grouped[sectionName].push(chapter);
      
      console.log(`📋 Chapter ${index + 1}: "${chapter.title}" -> Section: "${sectionName}" (sectionId: ${chapter.sectionId}, order: ${chapter.order})`);
    });
    
    // Sort chapters within each section by their order field
    Object.keys(grouped).forEach(section => {
      grouped[section].sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });
      console.log(`  📁 Section "${section}": ${grouped[section].length} chapters (sorted by order)`);
    });
    
    console.log('📊 Final grouped sections:', Object.keys(grouped));
    return grouped;
  };

  // Function to sort sections in logical order
  const sortSections = (sections) => {
    // Use the original sections order if available
    if (originalSections && originalSections.length > 0) {
      const originalSectionTitles = originalSections.map(section => section.title);
      
      return sections.sort((a, b) => {
        const aIndex = originalSectionTitles.indexOf(a);
        const bIndex = originalSectionTitles.indexOf(b);
        
        // If both sections are in the original order, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        
        // If only one is in the original order, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        
        // If neither is in the original order, sort alphabetically
        return a.localeCompare(b);
      });
    }
    
    // Fallback to hardcoded order if no original sections
    const sectionOrder = [
      ' Introduction',
      'My Early Life',
      'Post-Marriage Life', 
      'Why It All Failed for Me',
      'The Awakening',
      'Simplifying, One Bite at a Time',
      'How Running Transformed Me',
      'Living KISS, Sustaining for Life',
      'Other Chapters'
    ];
    
    return sections.sort((a, b) => {
      const aIndex = sectionOrder.indexOf(a);
      const bIndex = sectionOrder.indexOf(b);
      
      // If both sections are in the order list, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // If only one is in the order list, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      // If neither is in the order list, sort alphabetically
      return a.localeCompare(b);
    });
  };

  // Function to expand all sections
  const expandAllSections = () => {
    const groupedChapters = groupChaptersBySection(customizedChapters);
    const sections = Object.keys(groupedChapters);
    const allOpen = {};
    sections.forEach(section => {
      allOpen[section] = true;
    });
    setOpenSections(allOpen);
  };

  // Function to collapse all sections
  const collapseAllSections = () => {
    setOpenSections({});
  };

  // Function to toggle all sections
  const toggleAllSections = () => {
    const groupedChapters = groupChaptersBySection(customizedChapters);
    const sections = Object.keys(groupedChapters);
    const allCurrentlyOpen = sections.every(section => openSections[section]);
    
    if (allCurrentlyOpen) {
      collapseAllSections();
    } else {
      expandAllSections();
    }
  };

  const handleChapterSelect = (index) => {
    setSelectedChapterIdx(index);
  };

  const handleCommentSubmit = async () => {
    if (!newComment.trim()) return;
    
    try {
      setPostingComment(true);
      setCommentError(null);
      
      const token = localStorage.getItem('app_token');
      const response = await api.post(`/api/books/1/chapters/${customizedChapters[selectedChapterIdx].id}/comments`, {
        commentText: newComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setComments(prev => [...prev, response.data]);
      setNewComment('');
    } catch (err) {
      console.error('Error posting comment:', err);
      setCommentError(err.message);
    } finally {
      setPostingComment(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-title">Loading your personalized book...</div>
        <div className="loading-subtitle">This may take a few moments</div>
      </div>
    );
  }

  if (error) {
    const isCustomizationError = error.includes('customization settings');
    
    return (
      <div className="error-container">
        <div className="error-title">
          {isCustomizationError ? 'Customization Required' : 'Error loading customized book'}
        </div>
        <div className="error-message">{error}</div>
        {isCustomizationError ? (
          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={() => window.location.href = '/settings'}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              Complete Customization Settings
            </button>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          <button 
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!customizedChapters || customizedChapters.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-title">No customized book available</div>
        <div className="loading-subtitle">
          {isGenerating 
            ? `Generating your personalized book... ${generationProgress}% complete`
            : 'Click the button below to generate your personalized book based on your preferences.'
          }
        </div>
        {!isGenerating && (
          <button 
            onClick={handleGenerateBook}
            disabled={isGenerating}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              opacity: isGenerating ? 0.6 : 1
            }}
          >
            {isGenerating ? 'Generating...' : 'Generate My Personalized Book'}
          </button>
        )}
        {isGenerating && (
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <div style={{ 
              width: '100%', 
              height: '8px', 
              backgroundColor: '#e9ecef', 
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${generationProgress}%`,
                height: '100%',
                backgroundColor: '#28a745',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
              {generationProgress}% complete
            </div>
          </div>
        )}
      </div>
    );
  }

  const currentChapter = customizedChapters[selectedChapterIdx];
  const isFirstChapter = selectedChapterIdx === 0;
  const isLastChapter = selectedChapterIdx === customizedChapters.length - 1;

  const handlePrevious = () => {
    if (!isFirstChapter) {
      setSelectedChapterIdx(selectedChapterIdx - 1);
    }
  };

  const handleNext = () => {
    if (!isLastChapter) {
      setSelectedChapterIdx(selectedChapterIdx + 1);
    }
  };

  return (
    <div className="book-preview-app-container">
      <main className="book-preview-content">
        {/* Navigation Toggle */}
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <button
            onClick={() => setShowNavigation(!showNavigation)}
            className="toggle-button"
            style={{
              padding: '8px 16px',
              backgroundColor: '#16a085',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showNavigation ? 'Hide Navigation' : 'Show Navigation'}
          </button>
        </div>

        {/* Chapter Navigation - Top */}
        {showNavigation && customizedChapters.length > 0 && (
          <div style={{ 
            width: '100%',
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              margin: '0 0 16px 0', 
              fontSize: '16px', 
              fontWeight: '600',
              color: '#495057',
              borderBottom: '1px solid #dee2e6',
              paddingBottom: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>Chapters ({customizedChapters.length})</span>
              <button
                onClick={toggleAllSections}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#5a6268';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#6c757d';
                }}
              >
                Toggle All
              </button>
            </h3>
            <div className="chapter-navigation">
              <div className="chapter-nav-scroll" style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '400px',
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingBottom: '8px'
              }}>
                {(() => {
                  const groupedChapters = groupChaptersBySection(customizedChapters);
                  const sections = Object.keys(groupedChapters);
                  
                  return sortSections(sections).map(sectionName => {
                    const sectionChapters = groupedChapters[sectionName];
                    const isSectionOpen = !!openSections[sectionName];
                    
                    return (
                      <div key={sectionName} className="section-group" style={{ 
                        width: '100%',
                        marginBottom: '8px'
                      }}>
                        <div 
                          className="section-header"
                          onClick={() => setOpenSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }))}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            marginBottom: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#495057',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#e9ecef';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#f8f9fa';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ 
                              marginRight: '8px', 
                              transition: 'transform 0.2s', 
                              transform: isSectionOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                              fontSize: '16px'
                            }}>
                              ▶
                            </span>
                            <span>{sectionName}</span>
                          </div>
                          <span style={{ 
                            fontSize: '11px', 
                            color: '#6c757d',
                            backgroundColor: '#e9ecef',
                            padding: '1px 4px',
                            borderRadius: '3px'
                          }}>
                            {sectionChapters.length}
                          </span>
                        </div>
                        
                        {isSectionOpen && (
                          <div className="chapter-buttons" style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap',
                            gap: '6px',
                            padding: '0 8px'
                          }}>
                            {sectionChapters.map((chapter, index) => {
                              const globalIndex = customizedChapters.findIndex(c => c.id === chapter.id);
                              return (
                                <button
                                  key={chapter.id}
                                  className={`chapter-button ${selectedChapterIdx === globalIndex ? 'selected' : ''}`}
                                  onClick={() => setSelectedChapterIdx(globalIndex)}
                                  style={{
                                    padding: '6px 10px',
                                    textAlign: 'left',
                                    backgroundColor: selectedChapterIdx === globalIndex ? '#e6e6ff' : '#ffffff',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: selectedChapterIdx === globalIndex ? '600' : '400',
                                    color: selectedChapterIdx === globalIndex ? '#495057' : '#6c757d',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    lineHeight: '1.3',
                                    minWidth: '120px',
                                    maxWidth: '200px'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (selectedChapterIdx !== globalIndex) {
                                      e.target.style.backgroundColor = '#f8f9fa';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (selectedChapterIdx !== globalIndex) {
                                      e.target.style.backgroundColor = selectedChapterIdx === globalIndex ? '#e6e6ff' : '#ffffff';
                                    }
                                  }}
                                  title={chapter.title}
                                >
                                  {chapter.title}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="book-preview-main" style={{ width: '100%' }}>
          {currentChapter && (
            <>
              {/* Chapter Banner Image or Title */}
              {currentChapter.image_url ? (
                <div 
                  className="chapter-header-image"
                  style={{
                    backgroundImage: `url(${currentChapter.image_url})`
                  }}
                >
                  <div className="chapter-header-overlay" />
                  <h2 className="book-chapter-title chapter-title">
                    {currentChapter.title}
                  </h2>
                </div>
              ) : (
                <h2 className="book-chapter-title" style={{ textAlign: 'center', margin: '32px 0 24px 0' }}>
                  {currentChapter.title}
                </h2>
              )}
              
              <div
                className="book-preview-chapter-content"
                dangerouslySetInnerHTML={{ __html: currentChapter.content || '<em>No content</em>' }}
                onClick={(e) => {
                  if (e.target.tagName === 'IMG') {
                    handleImageClick(e.target.src);
                  }
                }}
              />
              
              {/* Navigation Buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px', padding: '0 32px' }}>
                <button
                  onClick={handlePrevious}
                  disabled={isFirstChapter}
                  className="btn-secondary"
                  style={{
                    opacity: isFirstChapter ? 0.5 : 1,
                    cursor: isFirstChapter ? 'not-allowed' : 'pointer'
                  }}
                >
                  ← Previous
                </button>
                
                <span style={{ fontSize: '14px', color: '#666' }}>
                  Chapter {selectedChapterIdx + 1} of {customizedChapters.length}
                </span>
                
                <button
                  onClick={handleNext}
                  disabled={isLastChapter}
                  className="btn-primary"
                  style={{
                    opacity: isLastChapter ? 0.5 : 1,
                    cursor: isLastChapter ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>

        {/* Comments Section */}
        <div className="book-preview-comments">
          <CommentsSection 
            comments={comments}
            newComment={newComment}
            setNewComment={setNewComment}
            isLoadingComments={isLoadingComments}
            postingComment={postingComment}
            commentError={commentError}
            handleCommentSubmit={handleCommentSubmit}
          />
        </div>
      </main>
      <Navbar onMenuOpen={() => {}} />
      
      {/* Image Modal */}
      {selectedImage && (
        <ImageModal 
          imageUrl={selectedImage} 
          onClose={() => setSelectedImage(null)} 
        />
      )}
    </div>
  );
};

export default CustomizedBookPage; 